import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/Tabs';
import { AudioRecorder } from './advanced/AudioRecorder';
import { CompingEditor } from './advanced/CompingEditor';
import { MarkerTrack } from './advanced/MarkerTrack';
import { TempoTrack } from './advanced/TempoTrack';
import { TimeStretchEditor } from './advanced/TimeStretchEditor';
import { AudioSlicer } from './advanced/AudioSlicer';
import { QuantizeEditor } from './advanced/QuantizeEditor';
import { MIDILearn } from './advanced/MIDILearn';
import { MacroControls } from './advanced/MacroControls';
import { RoutingMatrix } from './advanced/RoutingMatrix';
import { TrackFreezing } from './advanced/TrackFreezing';
import { PluginDelayCompensation } from './advanced/PluginDelayCompensation';
import { StemExporter } from './advanced/StemExporter';
import { ClipLauncher } from './advanced/ClipLauncher';

interface AdvancedFeaturesPanelProps {
  onClose: () => void;
}

type TabValue = 'recording' | 'arrangement' | 'audio' | 'midi' | 'mixer' | 'export' | 'performance';

const STORAGE_KEY = 'advanced-features-active-tab';

export const AdvancedFeaturesPanel = ({ onClose }: AdvancedFeaturesPanelProps) => {
  // Load active tab from localStorage or default to 'recording'
  const [activeTab, setActiveTab] = useState<TabValue>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return (saved as TabValue) || 'recording';
  });

  // Persist active tab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, activeTab);
  }, [activeTab]);

  // Handle Escape key to close panel
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="bg-zinc-900 border border-zinc-700 rounded-lg w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-700">
            <h2 className="text-xl font-semibold text-white">Advanced Features</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
              aria-label="Close Advanced Features Panel"
            >
              <X className="w-5 h-5 text-zinc-400" />
            </button>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as TabValue)}>
            <TabsList className="px-6">
              <TabsTrigger value="recording">Recording</TabsTrigger>
              <TabsTrigger value="arrangement">Arrangement</TabsTrigger>
              <TabsTrigger value="audio">Audio</TabsTrigger>
              <TabsTrigger value="midi">MIDI</TabsTrigger>
              <TabsTrigger value="mixer">Mixer</TabsTrigger>
              <TabsTrigger value="export">Export</TabsTrigger>
              <TabsTrigger value="performance">Performance</TabsTrigger>
            </TabsList>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto px-6">
              <TabsContent value="recording">
                <div className="space-y-6">
                  <AudioRecorder />
                  <CompingEditor />
                </div>
              </TabsContent>

              <TabsContent value="arrangement">
                <div className="space-y-6">
                  <MarkerTrack />
                  <TempoTrack />
                </div>
              </TabsContent>

              <TabsContent value="audio">
                <div className="space-y-6">
                  <TimeStretchEditor />
                  <AudioSlicer />
                  <QuantizeEditor />
                </div>
              </TabsContent>

              <TabsContent value="midi">
                <div className="space-y-6">
                  <MIDILearn />
                  <MacroControls />
                </div>
              </TabsContent>

              <TabsContent value="mixer">
                <div className="space-y-6">
                  <RoutingMatrix />
                  <TrackFreezing />
                  <PluginDelayCompensation />
                </div>
              </TabsContent>

              <TabsContent value="export">
                <StemExporter />
              </TabsContent>

              <TabsContent value="performance">
                <ClipLauncher />
              </TabsContent>
            </div>
          </Tabs>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
